import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  memo,
} from 'react';
import { Row, Col, Typography, Form, message } from 'antd';
import moment from 'moment';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useHistory, useParams } from 'react-router-dom';
import { sendSearchEventAnalytics } from '../../../analytics/analytics';
import API from '../../../api/API';
import UserContext from '../../../contexts/user.context';
import { Filters, Table, CommentDrawer } from './index';
import { SortOrder } from '../../../models/sortOrder';
import './ViewAllEvents.css';

const { Title, Text } = Typography;

const ViewAllEvents: React.FC = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>(undefined);
  const [showComments, setShowComments] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');
  const [form] = Form.useForm();
  const { user } = useContext(UserContext);

  const history = useHistory();
  const { eventId } = useParams<{ eventId?: string | undefined }>();

  /*
  Returns an array of principal attendees by sorting through JSON response
  */
  const getPrincipalAttendees = (attendees: any[]) => {
    const principal: any[] = [];

    attendees.forEach((a: any) => {
      if (a.orgLevel === 'principal') {
        a.orgAttendees.forEach((org: any) => {
          org.staff.forEach((st: any) => principal.push(st));
        });
      }
    });

    return principal;
  };

  /*
  Returns an array of additional attendees by sorting through JSON response
  */
  const getAgencyAttendees = (attendees: any[]) => {
    const agency: any[] = [];

    attendees.forEach((a: any) => {
      if (a.orgLevel === 'agency' || a.orgLevel === 'region') {
        a.orgAttendees.forEach((org: any) => {
          const orgInitials = a.orgLevel === 'region' ? 'OASH' : org.staffOrg;
          let staff = '';

          org.staff.forEach((st: any) => {
            staff = `${staff}${st}, `;
          });

          agency.push(`${orgInitials}: ${staff.slice(0, -2)}`);
        });
      }
    });

    return agency;
  };

  /*
  Returns the query string from form data
  */
  const createSearchParams = useCallback(() => {
    const formData = form.getFieldsValue();
    let email = '';

    Object.keys(formData).forEach((key: any) => {
      if (formData[key] === undefined) {
        formData[key] = '';
      } else if (key === 'start' || key === 'end') {
        formData[key] = formData[key].format('MM-DD-YYYY');
      } else if (key === 'flagged') {
        formData[key] = formData[key] ? '1' : '';
      } else if (key === 'notReviewed') {
        formData[key] = formData[key] ? '0' : '';
      }
    });

    const {
      q,
      start,
      end,
      state,
      agencies,
      format,
      attendees,
      ehe,
      jurisdictions,
      rsp,
      myEvents,
      flagged,
      notReviewed,
      limit,
    } = formData;

    if (myEvents === true) {
      email = user.username;
    }

    const params = {
      id: eventId || '',
      q,
      start,
      end,
      state,
      agencies: agencies.length ? agencies : '',
      format,
      attendees: attendees.length ? attendees : '',
      ehe,
      jurisdictions: jurisdictions.length ? jurisdictions : '',
      rsp,
      user: email,
      flagged,
      reviewed: notReviewed,
      limit,
      offset: 0,
      sort: '',
    };

    return params;
  }, [form, user.username, eventId]);

  /*
  Returns a formatted object array for the table data source
  */
  const formatTableData = useCallback((data: any): any[] => {
    const respTableData: any[] = [];
    const currentTime = moment().utc();
    const startDate = moment().utc().subtract(7, 'days');

    data.forEach((row: any) => {
      const newObject = {
        id: row.id,
        latestRevisionId: row.latestRevisionId,
        lastUpdatedBy: row.lastUpdatedBy,
        lastUpdatedOn: moment(row.lastUpdatedOn).format('MM/DD/YY'),
        createdBy: row.createdBy,
        createdOn: moment(row.createdOn).format('MM/DD/YY'),
        reviewed: row.reviewed,
        flagged: row.flagged,
        occurredOn:
          row.occurredOn === null
            ? 'TBD'
            : moment(row.occurredOn).utc().format('MM/DD/YY'),
        status:
          row.occurredOn === null || currentTime < moment(row.occurredOn).utc()
            ? 'In Progress'
            : 'Complete',
        description: row.description,
        format: row.format,
        additionalAttendees: row.additionalAttendees,
        jurisdictionVisit: row.jurisdictionVisit,
        jurisdictionsByState: row.jurisdictionsByState,
        readySetPrep: row.readySetPrep,
        location: row.location,
        stateAbbreviation: row.stateAbbreviation,
        otherLocalitiesReached:
          row.otherLocalitiesReached === null
            ? 'N/A'
            : row.otherLocalitiesReached,
        fullCount: row.fullCount,
        congressMembers: row.congressMembers,
        audience: row.audience,
        agencies: row.agencies,
        principal: getPrincipalAttendees(row.attendees),
        agencyAttendees: getAgencyAttendees(row.attendees),
        comments: row.comments,
        recentEvent: !!(
          moment(row.lastUpdatedOn).utc() >= startDate &&
          moment(row.lastUpdatedOn).utc() <= currentTime
        ),
        following: row.following,
      };

      respTableData.push(newObject);
    });

    return respTableData;
  }, []);

  /*
  Gets the query values from the server and updates the table rows.
  */
  const fetchData = useCallback(
    async (params: any) => {
      setLoading(true);
      const resp = await API.get('/event/search', { params });
      const newTableData = formatTableData(resp.data);
      setTableData(newTableData);

      let newTotal = 0;

      if (newTableData.length !== 0) {
        newTotal = Number(newTableData[0].fullCount);
      }

      setTotal(newTotal);

      setLoading(false);
    },
    [formatTableData],
  );

  useEffect(() => {
    document.title = 'EHE Tracker - View All Events';
  }, []);

  useEffect(() => {
    if (eventId) {
      setShowComments(true);
      setSelectedComment(eventId);
    }
  }, [eventId]);

  useEffect(() => {
    try {
      fetchData({
        id: eventId || '',
        q: '',
        start: '',
        end: '',
        state: '',
        agencies: '',
        format: '',
        attendees: '',
        ehe: '',
        jurisdictions: '',
        rsp: '',
        user: '',
        flagged: '',
        reviewed: '',
        limit: 25,
        offset: 0,
        sort: '',
      });
    } catch {
      setLoading(false);
      message.error('Looks like something went wrong.');
    }
  }, [eventId, fetchData]);

  /*
  Takes form data and updates table
  */
  const onSearch = useCallback(() => {
    if (eventId) history.push('/events');

    try {
      setCurrentPage(1);
      setSortOrder(undefined);
      const query = createSearchParams();
      fetchData(query);
      sendSearchEventAnalytics(query);
    } catch {
      setLoading(false);
    }
  }, [eventId, history, createSearchParams, fetchData]);

  const onReset = useCallback(() => {
    form.resetFields();
    onSearch();
  }, [form, onSearch]);

  /*
  Sets new page size and queries the server for new table data
  */
  const onSelectChange = useCallback(
    (value: any) => {
      setPageSize(value);
      onSearch();
    },
    [onSearch],
  );

  /*
  Creates a new query string with form data and replaces the offset number with current pagination number.
  Then sends the query to the server
  */
  const onTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setSortOrder(sorter.order);
      setCurrentPage(pagination.current);
      const limit = form.getFieldValue('limit');

      try {
        const params = createSearchParams();

        if (sorter.order === 'ascend') params.sort = 'ASC';
        else if (sorter.order === 'descend') params.sort = 'DESC';

        params.offset = limit * (pagination.current - 1);
        fetchData(params);
      } catch {
        setLoading(false);
      }
    },
    [createSearchParams, fetchData, form],
  );

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await API.delete(`/event/${id}`);
        const params = createSearchParams();
        params.offset = params.limit * (currentPage - 1);
        fetchData(params);
      } catch {
        setLoading(false);
      }
    },
    [createSearchParams, currentPage, fetchData],
  );

  const onTableCheckboxChange = useCallback(
    (e: CheckboxChangeEvent, currentEventId: any) => {
      const checkboxValue = e.target.checked ? 1 : 0;

      const reqBody = {
        id: currentEventId,
        ...(e.target.name === 'reviewedCheckbox' && {
          reviewed: checkboxValue,
        }),
        ...(e.target.name === 'flaggedCheckbox' && {
          flagged: checkboxValue,
        }),
      };

      API.put(`/event/${currentEventId}`, reqBody);
    },
    [],
  );

  const onTableFollowCheckboxChange = useCallback(
    (e: CheckboxChangeEvent, currentEventId: any) => {
      if (e.target.checked) API.post(`/event/${currentEventId}/follow`);
      else API.delete(`/event/${currentEventId}/unfollow`);
    },
    [],
  );

  /*
   * Generates the pagination display string.
   */
  const renderTotalEventsText = () => {
    if (total === 0) return 'Showing 0 Events';

    const firstNumber = (currentPage - 1) * pageSize + 1;
    const secondNumber =
      pageSize * currentPage > total ? total : pageSize * currentPage;

    return `Showing ${firstNumber} - ${secondNumber} of ${total} Events`;
  };

  const onCommentClick = useCallback((id: string) => {
    setSelectedComment(id);
    setShowComments(true);
  }, []);

  const onCommentClose = () => {
    setSelectedComment('');
    setShowComments(false);
  };

  return (
    <Row className={showComments ? 'ViewAllEvents-show-comments' : ''}>
      <Col span={24}>
        <Title>View All Events</Title>
        {/* 
        Input fields wrapped in Form Component to get onSearch and onReset functionality
      */}
        <Filters
          form={form}
          onSearch={onSearch}
          onReset={onReset}
          onSelectChange={onSelectChange}
          createSearchParams={createSearchParams}
        />
        <Row>
          <Text type="secondary" className="ViewAllEvents-pagination-text">
            {renderTotalEventsText()}
          </Text>
        </Row>
        {/*
        Table
      */}
        <Table
          tableData={tableData}
          total={total}
          pageSize={pageSize}
          current={currentPage}
          sortOrder={sortOrder}
          loading={loading}
          selectedRow={selectedComment}
          onTableChange={onTableChange}
          onDelete={onDelete}
          onCommentClick={onCommentClick}
          onTableCheckboxChange={onTableCheckboxChange}
          onTableFollowCheckboxChange={onTableFollowCheckboxChange}
        />
        <CommentDrawer
          visible={showComments}
          onClose={onCommentClose}
          eventId={selectedComment}
        />
      </Col>
    </Row>
  );
};

export default memo(ViewAllEvents);
